import { useState, useEffect, useCallback } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Plus,
  Sparkles,
  Settings,
  Users,
  Bot,
  Brain,
  FileText,
  Globe,
  Cpu,
  Check,
  X,
  ChevronDown,
  ChevronUp,
  Save,
  AlertTriangle,
  ShieldAlert,
} from "lucide-react";
import {
  apiv2,
  type ProjectOut,
  type AgentRegistryItem,
  type UserOut,
  type ProjectMemberRole,
  type LLMProviderInfo,
} from "../apiv2";
import { useAuth } from "../auth";
import { rulesApi, type ProjectRuleRole } from "../api/rules";
import GreenTendrils from "./GreenTendrils";
import SharedDBConfig from "./SharedDBConfig";
import ProjectConnectionsManager from "./ProjectConnectionsManager";
import EngagementConfig from "./EngagementConfig";
import PurgeProjectDataDialog from "./PurgeProjectDataDialog";

interface Props {
  projectId: string;
  onBack: () => void;
  onLaunchAgents: () => void;
}

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------
function Section({
  title,
  icon,
  defaultOpen = true,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className="glass rounded-2xl overflow-hidden"
      style={{
        background: "rgba(28,28,42,0.5)",
        border: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-white/[0.02] transition"
      >
        {icon}
        <span className="text-sm font-bold text-white flex-1">{title}</span>
        {open ? (
          <ChevronUp size={14} className="text-white/30" />
        ) : (
          <ChevronDown size={14} className="text-white/30" />
        )}
      </button>
      {open && <div className="px-5 pb-5 pt-1 space-y-4">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function ProjectSetup({
  projectId,
  onBack,
  onLaunchAgents,
}: Props) {
  const { user: me, isAdmin, isProjectUser, hasPermission } = useAuth();

  // Project state
  const [project, setProject] = useState<ProjectOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);

  // Edit fields
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [requirements, setRequirements] = useState("");
  const [sourceDataInfo, setSourceDataInfo] = useState("");
  const [domain, setDomain] = useState("");
  const [techStack, setTechStack] = useState("");

  // Agent selection
  const [agentRegistry, setAgentRegistry] = useState<AgentRegistryItem[]>([]);
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());

  // LLM config
  const [llmProviders, setLlmProviders] = useState<LLMProviderInfo[]>([]);
  const [llmProvider, setLlmProvider] = useState("anthropic");
  const [llmModel, setLlmModel] = useState("claude-sonnet-4-20250514");
  const [llmApiKey, setLlmApiKey] = useState("");
  const [showLlmKey, setShowLlmKey] = useState(false);
  const [llmTemp, setLlmTemp] = useState(0.7);
  const [llmMaxTokens, setLlmMaxTokens] = useState(4096);
  const [useGlobalLlm, setUseGlobalLlm] = useState(true); // default: use global config
  // Provider-specific fields
  const [awsRegion, setAwsRegion] = useState("us-east-1");
  const [awsAccessKeyId, setAwsAccessKeyId] = useState("");
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState("");
  const [readTimeout, setReadTimeout] = useState(300);
  const [azureEndpoint, setAzureEndpoint] = useState("");
  const [azureDeployment, setAzureDeployment] = useState("");
  const [azureApiVersion, setAzureApiVersion] = useState("2024-12-01-preview");

  // Members
  const [allUsers, setAllUsers] = useState<UserOut[]>([]);
  const [addUserId, setAddUserId] = useState("");
  const [addUserRole, setAddUserRole] = useState<ProjectMemberRole>("member");

  const canEdit = isAdmin || isProjectUser;
  // Same rule as the server (PUT /dq-rules/project-roles): the project's owner, or a tenant administrator.
  const tenantAdmin =
    isAdmin ||
    (me?.permissions ?? []).some((p) =>
      ["*", "admin:*", "admin:tenant"].includes(p),
    );
  const canAssignRuleRoles =
    tenantAdmin ||
    (Boolean(project?.created_by) && project?.created_by === me?.id);

  // Load data
  const loadAll = useCallback(async () => {
    try {
      const [proj, agents, provs] = await Promise.all([
        apiv2.getProject(projectId),
        apiv2.listAgents(false),
        apiv2.listProviders(),
      ]);
      setLlmProviders(provs);
      setProject(proj);
      setName(proj.name);
      setDescription(proj.description);
      setRequirements(proj.requirements);
      setSourceDataInfo(proj.source_data_info);
      setDomain(proj.domain);
      setTechStack(proj.tech_stack);
      setAgentRegistry(agents);
      setSelectedAgents(new Set(proj.agents.map((a) => a.agent_id)));

      if (proj.llm_config) {
        setUseGlobalLlm(false);
        setLlmProvider(proj.llm_config.provider);
        setLlmModel(proj.llm_config.model_name);
        setLlmTemp(proj.llm_config.temperature);
        setLlmMaxTokens(proj.llm_config.max_tokens);
        try {
          const extra = JSON.parse(proj.llm_config.additional_config || "{}");
          setAwsRegion(extra.aws_region || "us-east-1");
          setAwsAccessKeyId(extra.aws_access_key_id || "");
          setAwsSecretAccessKey(extra.aws_secret_access_key || "");
          setReadTimeout(
            extra.read_timeout ? parseInt(extra.read_timeout) : 300,
          );
          setAzureEndpoint(extra.azure_endpoint || "");
          setAzureDeployment(extra.azure_deployment || "");
          setAzureApiVersion(extra.azure_api_version || "2024-12-01-preview");
        } catch {
          /* ignore */
        }
      }

      // Load users for member management (admin/project_user only)
      if (isAdmin || isProjectUser) {
        try {
          const users = await apiv2.listUsers();
          setAllUsers(users);
        } catch {
          // Non-admin can't list all users — that's fine
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load project");
    } finally {
      setLoading(false);
    }
  }, [projectId, isAdmin, isProjectUser]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Save project details
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      // 1. Update project fields
      const updated = await apiv2.updateProject(projectId, {
        name: name.trim(),
        description: description.trim(),
        requirements: requirements.trim(),
        source_data_info: sourceDataInfo.trim(),
        domain: domain.trim(),
        tech_stack: techStack.trim(),
      });
      setProject(updated);

      // 2. Update agent selection
      await apiv2.enableAgents(projectId, Array.from(selectedAgents));

      // 3. Update LLM config
      if (useGlobalLlm) {
        // Delete project-level config so it falls back to global
        await apiv2.deleteLLMConfig(projectId);
      } else {
        const additionalConfig: Record<string, string> = {};
        if (llmProvider === "aws_bedrock") {
          additionalConfig.aws_region = awsRegion;
          if (awsAccessKeyId)
            additionalConfig.aws_access_key_id = awsAccessKeyId;
          if (awsSecretAccessKey)
            additionalConfig.aws_secret_access_key = awsSecretAccessKey;
          additionalConfig.read_timeout = String(readTimeout);
        }
        if (llmProvider === "azure_openai") {
          if (azureEndpoint) additionalConfig.azure_endpoint = azureEndpoint;
          if (azureDeployment)
            additionalConfig.azure_deployment = azureDeployment;
          additionalConfig.azure_api_version = azureApiVersion;
        }
        await apiv2.setLLMConfig(projectId, {
          provider: llmProvider,
          model_name: llmModel,
          api_key: llmApiKey || undefined,
          temperature: llmTemp,
          max_tokens: llmMaxTokens,
          additional_config: additionalConfig,
        });
      }

      setSuccess("Project saved successfully");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save project");
    } finally {
      setSaving(false);
    }
  };

  // Add member
  const handleAddMember = async () => {
    if (!addUserId) return;
    setError(null);
    try {
      await apiv2.addMember(projectId, addUserId, addUserRole);
      setAddUserId("");
      await loadAll();
      setSuccess("Member added");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add member");
    }
  };

  // Project rule role (DQ rule governance): assigned by the project owner or a tenant admin, audited server-side.
  const handleRuleRole = async (userId: string, value: string) => {
    setError(null);
    try {
      await rulesApi.projectRoles.assign(
        projectId,
        userId,
        value === "" ? null : (value as ProjectRuleRole),
      );
      await loadAll();
      setSuccess("Rule role updated");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update the rule role",
      );
    }
  };

  // Remove member
  const handleRemoveMember = async (userId: string) => {
    try {
      await apiv2.removeMember(projectId, userId);
      await loadAll();
      setSuccess("Member removed");
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to remove member");
    }
  };

  // Toggle agent
  const toggleAgent = (agentId: string) => {
    setSelectedAgents((prev) => {
      const next = new Set(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.add(agentId);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] relative">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <Loader2
          size={24}
          className="animate-spin text-brand-500 relative z-10"
        />
      </div>
    );
  }

  // Members already in project
  const memberIds = new Set(project?.members.map((m) => m.user_id) ?? []);
  const availableUsers = allUsers.filter(
    (u) => u.is_active && !memberIds.has(u.id),
  );

  return (
    <div className="overflow-y-auto custom-scrollbar h-full relative bg-surface-500">
      <div className="absolute inset-0 dot-grid" />
      <div className="glow-orb glow-orb-1" />
      <div className="glow-orb glow-orb-2" />
      <GreenTendrils />

      <div className="relative z-10 max-w-4xl mx-auto px-6 py-10 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4 animate-fade-in">
          <div className="flex items-center gap-5">
            <button
              onClick={onBack}
              className="p-2.5 rounded-xl border border-white/8 hover:border-brand-500/30 transition-all hover:bg-brand-500/5"
              style={{ background: "rgba(28,28,42,0.5)" }}
            >
              <ArrowLeft size={18} className="text-white/50" />
            </button>
            <div>
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-brand-500/30 text-brand-400 text-xs font-medium mb-3"
                style={{ background: "rgba(134,188,37,0.08)" }}
              >
                <Sparkles size={12} />
                Project Setup
              </div>
              <h1 className="text-3xl font-black tracking-tight">
                <span className="gradient-text-green">
                  {project?.name || "Project"}
                </span>
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {canEdit && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl text-sm font-bold text-white flex items-center gap-2 disabled:opacity-50"
                style={{
                  background: "linear-gradient(135deg, #86BC25, #6B9B1E)",
                  boxShadow: "0 0 20px rgba(134,188,37,0.25)",
                }}
              >
                {saving ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Save size={14} />
                )}
                Save All
              </button>
            )}
            <button
              onClick={onLaunchAgents}
              className="px-5 py-2.5 rounded-xl text-sm font-bold text-white flex items-center gap-2 border border-brand-500/30 hover:border-brand-500/60 hover:bg-brand-500/10 transition"
            >
              <ArrowRight size={14} /> Launch Agents
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-sm text-red-300 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={14} />
              {error}
            </div>
            <button
              onClick={() => setError(null)}
              className="text-xs text-red-500"
            >
              dismiss
            </button>
          </div>
        )}
        {success && (
          <div className="p-3 bg-brand-500/10 border border-brand-500/30 rounded-xl text-sm text-brand-300 flex items-center gap-2">
            <Check size={14} /> {success}
          </div>
        )}

        {/* ── Quick Status ──────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 animate-fade-in">
          {[
            {
              label: "Agents",
              value: `${selectedAgents.size}`,
              icon: <Bot size={14} className="text-purple-400" />,
              color: "purple",
            },
            {
              label: "Members",
              value: `${project?.members.length || 0}`,
              icon: <Users size={14} className="text-teal-400" />,
              color: "teal",
            },
            {
              label: "LLM",
              value: project?.llm_config?.has_api_key
                ? "Configured"
                : "Not Set",
              icon: <Brain size={14} className="text-amber-400" />,
              color: project?.llm_config?.has_api_key ? "green" : "yellow",
            },
            {
              label: "Mode",
              value:
                project?.execution_mode === "autonomous"
                  ? "Autonomous"
                  : "Direct",
              icon: <Cpu size={14} className="text-brand-500" />,
              color: "brand",
            },
          ].map((stat) => (
            <div
              key={stat.label}
              className="glass rounded-xl p-3 flex items-center gap-3"
              style={{
                background: "rgba(28,28,42,0.4)",
                border: "1px solid rgba(255,255,255,0.04)",
              }}
            >
              <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
                {stat.icon}
              </div>
              <div>
                <div className="text-[11px] text-white/30 uppercase tracking-wider">
                  {stat.label}
                </div>
                <div className="text-xs font-bold text-white">{stat.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── 1. Project Details ─────────────────────── */}
        <Section
          title="Project Details"
          icon={<FileText size={16} className="text-brand-500" />}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-white/40 mb-1">
                Project Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!canEdit}
                className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50 disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-xs text-white/40 mb-1">Domain</label>
              <input
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                disabled={!canEdit}
                placeholder="e.g., Finance, Healthcare, Retail"
                className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 disabled:opacity-50"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={!canEdit}
              rows={2}
              className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 disabled:opacity-50 resize-none"
              placeholder="Brief project description..."
            />
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">
              Tech Stack
            </label>
            <input
              type="text"
              value={techStack}
              onChange={(e) => setTechStack(e.target.value)}
              disabled={!canEdit}
              placeholder="e.g., Snowflake, dbt, Airflow, Tableau"
              className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 disabled:opacity-50"
            />
          </div>
        </Section>

        {/* ── 2. Requirements ────────────────────────── */}
        <Section
          title="Requirements"
          icon={<Globe size={16} className="text-blue-400" />}
        >
          <div>
            <label className="block text-xs text-white/40 mb-1">
              Business & Technical Requirements
            </label>
            <textarea
              value={requirements}
              onChange={(e) => setRequirements(e.target.value)}
              disabled={!canEdit}
              rows={6}
              className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 disabled:opacity-50 resize-y font-mono"
              placeholder="List your project requirements here...&#10;- Migrate all 42 tables from Oracle to Snowflake&#10;- Maintain referential integrity&#10;- Mask PII in non-production environments"
            />
          </div>
          <div>
            <label className="block text-xs text-white/40 mb-1">
              Source Data Information (JSON or free text)
            </label>
            <textarea
              value={sourceDataInfo}
              onChange={(e) => setSourceDataInfo(e.target.value)}
              disabled={!canEdit}
              rows={4}
              className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 disabled:opacity-50 resize-y font-mono"
              placeholder='{"source_db": "Oracle 19c", "tables": ["CUSTOMER", "ORDER"], "estimated_rows": "50M"}'
            />
          </div>
        </Section>

        {/* ── 2b. Source Database Configuration ────── */}
        <SharedDBConfig projectId={projectId} kind="source" canEdit={canEdit} />

        {/* ── 2c. Target Database Configuration ────── */}
        <SharedDBConfig projectId={projectId} kind="target" canEdit={canEdit} />

        {/* ── 2c-2. All saved connections (any agent, any db type) ── */}
        <ProjectConnectionsManager projectId={projectId} canEdit={canEdit} />

        {/* ── 2d. Engagement & DQ Rule Packs ────────── */}
        <EngagementConfig projectId={projectId} canEdit={canEdit} />

        {/* ── 3. Agent Selection ─────────────────────── */}
        <Section
          title="Agent Selection"
          icon={<Bot size={16} className="text-purple-400" />}
        >
          <p className="text-xs text-white/40 mb-3">
            Select which AI agents to enable for this project.{" "}
            {selectedAgents.size} selected.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {agentRegistry.map((agent) => {
              const selected = selectedAgents.has(agent.id);
              return (
                <button
                  key={agent.id}
                  onClick={() => canEdit && toggleAgent(agent.id)}
                  disabled={!canEdit}
                  className={`text-left p-4 rounded-xl border transition-all ${
                    selected
                      ? "border-brand-500/40 bg-brand-500/10"
                      : "border-white/5 bg-surface-300/30 hover:border-white/15"
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <div className="flex items-center gap-3 mb-1.5">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                        selected ? "bg-brand-500/20" : "bg-surface-200"
                      }`}
                    >
                      <Bot
                        size={16}
                        className={
                          selected ? "text-brand-500" : "text-white/30"
                        }
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-semibold text-white block truncate">
                        {agent.short_name}
                      </span>
                      <span className="text-[11px] text-white/30">
                        {agent.category}
                      </span>
                    </div>
                    {selected && (
                      <div className="w-5 h-5 rounded-full bg-brand-500 flex items-center justify-center flex-shrink-0">
                        <Check size={12} className="text-white" />
                      </div>
                    )}
                  </div>
                  <p className="text-[11px] text-white/40 line-clamp-2">
                    {agent.description}
                  </p>
                </button>
              );
            })}
          </div>
        </Section>

        {/* ── 4. LLM Configuration ──────────────────── */}
        {canEdit && (
          <Section
            title="LLM Configuration"
            icon={<Brain size={16} className="text-amber-400" />}
            defaultOpen={false}
          >
            {/* Use Global Config toggle */}
            <div className="flex items-center justify-between p-3 rounded-xl bg-surface-300/20 border border-white/5">
              <div>
                <div className="text-xs font-semibold text-white">
                  Use Global Configuration
                </div>
                <div className="text-[11px] text-white/40 mt-0.5">
                  Inherit the system-wide LLM settings configured by an admin
                </div>
              </div>
              <button
                onClick={() => setUseGlobalLlm(!useGlobalLlm)}
                className={`relative w-11 h-6 rounded-full transition-colors ${
                  useGlobalLlm ? "bg-brand-500" : "bg-white/10"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                    useGlobalLlm ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            {useGlobalLlm ? (
              <div className="p-4 rounded-xl bg-green-500/5 border border-green-500/10 text-sm text-green-400/80 flex items-center gap-2">
                <Check size={14} />
                This project will use the global LLM configuration. Admin can
                manage it from Settings → LLM Configuration.
              </div>
            ) : (
              <>
                {/* Provider selection cards */}
                <div>
                  <label className="block text-xs text-white/40 mb-2">
                    Provider
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {llmProviders.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => {
                          setLlmProvider(p.id);
                          if (p.models.length > 0) setLlmModel(p.models[0].id);
                          setLlmApiKey("");
                        }}
                        className={`p-3 rounded-xl border text-left transition-all ${
                          llmProvider === p.id
                            ? "border-brand-500/40 bg-brand-500/10"
                            : "border-white/5 bg-surface-300/30 hover:border-white/15"
                        }`}
                      >
                        <div className="text-xs font-semibold text-white">
                          {p.label}
                        </div>
                        <div className="text-[11px] text-white/30 mt-0.5">
                          {p.models.length} model
                          {p.models.length !== 1 ? "s" : ""}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Model dropdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-white/40 mb-1">
                      Model
                    </label>
                    <div className="relative">
                      <select
                        value={llmModel}
                        onChange={(e) => setLlmModel(e.target.value)}
                        className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white appearance-none focus:outline-none focus:border-brand-500/50"
                      >
                        {(
                          llmProviders.find((p) => p.id === llmProvider)
                            ?.models || []
                        ).map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        size={14}
                        className="absolute right-3 top-3 text-white/30 pointer-events-none"
                      />
                    </div>
                  </div>

                  {/* API Key (for anthropic, openai, azure_openai) */}
                  {(llmProvider === "anthropic" ||
                    llmProvider === "openai" ||
                    llmProvider === "azure_openai") && (
                    <div>
                      <label className="block text-xs text-white/40 mb-1">
                        API Key{" "}
                        {project?.llm_config?.has_api_key && !llmApiKey && (
                          <span className="text-green-400/60">(saved)</span>
                        )}
                      </label>
                      <div className="relative">
                        <input
                          type={showLlmKey ? "text" : "password"}
                          value={llmApiKey}
                          onChange={(e) => setLlmApiKey(e.target.value)}
                          placeholder={
                            project?.llm_config?.has_api_key
                              ? "••••••••••••"
                              : "Enter API key"
                          }
                          className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 pr-10"
                        />
                        <button
                          onClick={() => setShowLlmKey(!showLlmKey)}
                          className="absolute right-3 top-2 text-white/30 hover:text-white/60"
                        >
                          {showLlmKey ? (
                            <X size={14} />
                          ) : (
                            <Settings size={14} />
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* AWS Bedrock specific fields */}
                {llmProvider === "aws_bedrock" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-xl bg-surface-300/20 border border-white/5">
                    <div className="md:col-span-2">
                      <span className="text-[11px] font-semibold text-amber-400/80 uppercase tracking-wider">
                        AWS Bedrock Settings
                      </span>
                    </div>
                    <div>
                      <label className="block text-xs text-white/40 mb-1">
                        AWS Region
                      </label>
                      <input
                        type="text"
                        value={awsRegion}
                        onChange={(e) => setAwsRegion(e.target.value)}
                        className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-white/40 mb-1">
                        Access Key ID{" "}
                        <span className="text-white/20">
                          (optional — uses instance role if empty)
                        </span>
                      </label>
                      <input
                        type="text"
                        value={awsAccessKeyId}
                        onChange={(e) => setAwsAccessKeyId(e.target.value)}
                        placeholder="AKIA..."
                        className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs text-white/40 mb-1">
                        Secret Access Key
                      </label>
                      <div className="relative">
                        <input
                          type={showLlmKey ? "text" : "password"}
                          value={awsSecretAccessKey}
                          onChange={(e) =>
                            setAwsSecretAccessKey(e.target.value)
                          }
                          placeholder="Enter secret key"
                          className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50 pr-10"
                        />
                        <button
                          onClick={() => setShowLlmKey(!showLlmKey)}
                          className="absolute right-3 top-2 text-white/30 hover:text-white/60"
                        >
                          {showLlmKey ? (
                            <X size={14} />
                          ) : (
                            <Settings size={14} />
                          )}
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-white/40 mb-1">
                        Read Timeout{" "}
                        <span className="text-white/20">
                          (seconds — increase for large models)
                        </span>
                      </label>
                      <input
                        type="number"
                        min={30}
                        max={900}
                        value={readTimeout}
                        onChange={(e) =>
                          setReadTimeout(
                            Math.max(30, parseInt(e.target.value) || 300),
                          )
                        }
                        className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50"
                      />
                      <p className="text-[11px] text-white/20 mt-1">
                        Default: 300s (5 min). Opus models may need 300–600s.
                      </p>
                    </div>
                  </div>
                )}

                {/* Azure OpenAI specific fields */}
                {llmProvider === "azure_openai" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-xl bg-surface-300/20 border border-white/5">
                    <div className="md:col-span-2">
                      <span className="text-[11px] font-semibold text-blue-400/80 uppercase tracking-wider">
                        Azure OpenAI Settings
                      </span>
                    </div>
                    <div>
                      <label className="block text-xs text-white/40 mb-1">
                        Azure Endpoint
                      </label>
                      <input
                        type="text"
                        value={azureEndpoint}
                        onChange={(e) => setAzureEndpoint(e.target.value)}
                        placeholder="https://your-resource.openai.azure.com/"
                        className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-white/40 mb-1">
                        Deployment Name
                      </label>
                      <input
                        type="text"
                        value={azureDeployment}
                        onChange={(e) => setAzureDeployment(e.target.value)}
                        placeholder="gpt-4o"
                        className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white placeholder-white/20 focus:outline-none focus:border-brand-500/50"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-white/40 mb-1">
                        API Version
                      </label>
                      <input
                        type="text"
                        value={azureApiVersion}
                        onChange={(e) => setAzureApiVersion(e.target.value)}
                        className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50"
                      />
                    </div>
                  </div>
                )}

                {/* Temperature & Max Tokens */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-white/40 mb-1">
                      Temperature ({llmTemp})
                    </label>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={llmTemp}
                      onChange={(e) => setLlmTemp(parseFloat(e.target.value))}
                      className="w-full accent-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-white/40 mb-1">
                      Max Tokens
                    </label>
                    <input
                      type="number"
                      min={256}
                      max={200000}
                      value={llmMaxTokens}
                      onChange={(e) =>
                        setLlmMaxTokens(parseInt(e.target.value) || 4096)
                      }
                      className="w-full px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-sm text-white focus:outline-none focus:border-brand-500/50"
                    />
                  </div>
                </div>
              </>
            )}
          </Section>
        )}

        {/* ── 5. Team Members ───────────────────────── */}
        <Section
          title="Team Members"
          icon={<Users size={16} className="text-teal-400" />}
          defaultOpen={false}
        >
          {/* Current members */}
          <div className="space-y-2 mb-4">
            {project?.members.map((m) => (
              <div
                key={m.id}
                className="flex items-center gap-3 p-3 rounded-xl bg-surface-300/30 border border-white/5"
              >
                <div className="w-8 h-8 rounded-lg bg-brand-500/10 flex items-center justify-center">
                  <Users size={14} className="text-brand-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-semibold text-white block truncate">
                    {m.user_name}
                  </span>
                  <span className="text-[11px] text-white/30">
                    {m.user_email}
                  </span>
                </div>
                <span className="text-[11px] font-semibold text-white/50 px-2 py-0.5 rounded-full bg-surface-200 capitalize">
                  {m.role}
                </span>
                <label className="flex items-center gap-1 text-[11px] text-white/45">
                  Rule role
                  <select
                    aria-label={`Rule role for ${m.user_name || m.user_email}`}
                    value={m.rule_role ?? ""}
                    disabled={!canAssignRuleRoles}
                    title={
                      canAssignRuleRoles
                        ? undefined
                        : "Only the project owner or a tenant administrator can assign rule roles"
                    }
                    onChange={(e) =>
                      void handleRuleRole(m.user_id, e.target.value)
                    }
                    className="px-2 py-1 bg-surface-300/50 border border-white/8 rounded-md text-[11px] text-white focus:outline-none disabled:opacity-60"
                  >
                    <option value="">None (read only)</option>
                    <option value="dq_analyst">DQ Analyst</option>
                    <option value="data_steward">Data Steward</option>
                  </select>
                </label>
                {canEdit && m.role !== "owner" && (
                  <button
                    onClick={() => handleRemoveMember(m.user_id)}
                    className="p-1 text-white/20 hover:text-red-400 transition"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Add member */}
          {canEdit && availableUsers.length > 0 && (
            <div className="flex items-center gap-3">
              <select
                value={addUserId}
                onChange={(e) => setAddUserId(e.target.value)}
                className="flex-1 px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-xs text-white focus:outline-none focus:border-brand-500/50"
              >
                <option value="">Select a user to add...</option>
                {availableUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.full_name} ({u.email})
                  </option>
                ))}
              </select>
              <select
                value={addUserRole}
                onChange={(e) =>
                  setAddUserRole(e.target.value as ProjectMemberRole)
                }
                className="px-3 py-2 bg-surface-300/50 border border-white/8 rounded-lg text-xs text-white focus:outline-none"
              >
                <option value="member">Member</option>
                <option value="reviewer">Reviewer</option>
                <option value="owner">Owner</option>
              </select>
              <button
                onClick={handleAddMember}
                disabled={!addUserId}
                className="p-2 rounded-lg bg-brand-500/20 text-brand-500 hover:bg-brand-500/30 transition disabled:opacity-50"
              >
                <Plus size={14} />
              </button>
            </div>
          )}
        </Section>

        {/* ── 6. Danger Zone ─────────────────────────── */}
        {hasPermission("project:purge_data") && (
          <Section
            title="Danger Zone"
            icon={<ShieldAlert size={16} className="text-red-400" />}
            defaultOpen={false}
          >
            <div className="flex items-center justify-between gap-4 p-3 rounded-xl bg-red-500/8 border border-red-500/15">
              <div>
                <p className="text-xs font-semibold text-white">
                  Purge Client Data
                </p>
                <p className="text-[11px] text-white/40 mt-0.5">
                  Permanently deletes this project&apos;s client data, per
                  store, wherever a retention policy allows it. Separate from
                  Delete Project (which only archives) — this cannot be undone.
                </p>
              </div>
              <button
                onClick={() => setShowPurgeDialog(true)}
                className="shrink-0 px-3 py-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 border border-red-500/25 text-xs font-medium text-red-300 transition-colors flex items-center gap-1.5"
              >
                <AlertTriangle size={12} />
                Purge Client Data
              </button>
            </div>
          </Section>
        )}
      </div>

      {showPurgeDialog && project && (
        <PurgeProjectDataDialog
          projectId={projectId}
          projectName={project.name}
          onClose={() => setShowPurgeDialog(false)}
        />
      )}
    </div>
  );
}
